/**
 * How a canvas HTML block follows a clicked link, the same way for the
 * iframe card (the runtime posts the raw `href`) and the static card (its
 * own click handler in the shadow root).
 */

/** A fragment carrying viewport keys (`#x=&y=&z=&t=`) or node keys
 *  (`#node=`, `#nodes=`) is a canvas link for the host, not an in-block
 *  anchor. The same test as the runtime script's
 *  `isViewportFragment`; what the keys mean lives with the canvas
 *  (user-dashboard's `CANVAS_VIEWPORT_HASH_KEYS`). */
export function isViewportFragment(href: string): boolean {
  if (href.charAt(0) !== "#") return false;
  const params = new URLSearchParams(href.slice(1));
  return (
    params.has("x") ||
    params.has("y") ||
    params.has("z") ||
    params.has("node") ||
    params.has("nodes")
  );
}

/**
 * Follow a block link's raw `href`, resolved against the top URL (never an
 * iframe's srcdoc base). A same-origin link goes to `onNavigate` as its path,
 * query and hash; `onNavigate` may return `false` to decline it. A declined
 * same-origin link, or any link with no `onNavigate`, takes the fallback: an
 * https link opens in a new tab, a bare fragment is dropped (a new tab of the
 * same page is never what the block meant), anything else is ignored.
 *
 * Call it synchronously inside the click: the new tab needs the click's user
 * activation, which expires almost immediately.
 */
export function followBlockLink(
  href: string,
  onNavigate?: (to: string) => boolean | void,
): void {
  let parsed: URL;
  try {
    parsed = new URL(href, window.location.href);
  } catch {
    return;
  }
  if (
    onNavigate &&
    parsed.origin === window.location.origin &&
    onNavigate(parsed.pathname + parsed.search + parsed.hash) !== false
  ) {
    return;
  }
  if (href.charAt(0) === "#") return;
  if (parsed.protocol !== "https:") return;
  window.open(parsed.href, "_blank", "noopener,noreferrer");
}
